import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

const getCurrentPeriod = () => {
  const now = new Date();
  const y = now?.getFullYear();
  const m = String(now?.getMonth() + 1)?.padStart(2, '0');
  return `${y}-${m}`;
};

const getPrevPeriod = (period) => {
  if (!period) return '';
  const [y, m] = period?.split('-')?.map(Number);
  const date = new Date(y, m - 2, 1);
  const py = date?.getFullYear();
  const pm = String(date?.getMonth() + 1)?.padStart(2, '0');
  return `${py}-${pm}`;
};

const formatNum = (val) => {
  if (!val && val !== 0) return '—';
  return Number(val)?.toLocaleString('fr-FR');
};

// Toast component
const Toast = ({ toasts }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts?.map((t) => (
      <div
        key={t?.id}
        className="px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2"
        style={{
          backgroundColor: t?.type === 'success' ? '#16a34a' : '#dc2626',
          color: '#fff',
          fontFamily: 'Inter, sans-serif',
          minWidth: '260px',
        }}
      >
        {t?.type === 'success' ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="white" strokeWidth="1.5" />
            <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="white" strokeWidth="1.5" />
            <path d="M8 5v4M8 11v.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
        {t?.message}
      </div>
    ))}
  </div>
);

// Server badge
const ServerBadge = ({ serverId, servers }) => {
  const server = servers?.find((s) => s?.id === serverId);
  const color = server?.color || '#6b7280';
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-bold"
      style={{ backgroundColor: color + '22', color, border: `1px solid ${color}44` }}
    >
      {serverId}
    </span>
  );
};

// Inline editable cell
const InlineEditCell = ({ value, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => {
    setVal(value ?? '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef?.current) inputRef?.current?.focus();
  }, [editing]);

  const handleSave = () => {
    onSave(val === '' ? null : Number(val));
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') handleSave();
    if (e?.key === 'Escape') { setVal(value ?? ''); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          value={val}
          onChange={(e) => setVal(e?.target?.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => { setVal(value ?? ''); setEditing(false); }}
          className="w-24 px-2 py-1 rounded text-base text-white"
          style={{ backgroundColor: '#1a1a1a', border: '1px solid #E5202A', outline: 'none', fontFamily: 'Inter, sans-serif' }}
        />
        <button
          onMouseDown={(e) => { e?.preventDefault(); handleSave(); }}
          className="w-6 h-6 flex items-center justify-center rounded"
          style={{ backgroundColor: '#22c55e22', color: '#22c55e' }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <span
      className="cursor-pointer px-2 py-1 rounded hover:bg-white/5 transition-colors text-sm"
      style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Inter, sans-serif' }}
      onClick={() => setEditing(true)}
      title={t('admin.objectives.clickToEdit', 'Cliquer pour modifier')}
    >
      {value !== null && value !== undefined ? formatNum(value) : <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>}
    </span>
  );
};

// Edit Modal — period is read-only text display
const EditModal = ({ objective, servers, onClose, onSaved, addToast }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    objective_diamonds: objective?.objective_diamonds ?? '',
    objective_recruitment: objective?.objective_recruitment ?? '',
    diamonds_m1: objective?.diamonds_m1 ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');

    if (objective?.objective_id) {
      const { error: err } = await supabase?.from('manager_objectives')?.update({
        objective_diamonds: form?.objective_diamonds !== '' ? Number(form?.objective_diamonds) : null,
        objective_recruitment: form?.objective_recruitment !== '' ? Number(form?.objective_recruitment) : null,
        diamonds_m1: form?.diamonds_m1 !== '' ? Number(form?.diamonds_m1) : null,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', objective?.objective_id);
      setSaving(false);
      if (err) { setError(err?.message); return; }
    } else {
      const { error: err } = await supabase?.from('manager_objectives')?.upsert({
        manager_id: objective?.manager_id,
        server: objective?.server,
        period: objective?.period,
        objective_diamonds: form?.objective_diamonds !== '' ? Number(form?.objective_diamonds) : 0,
        objective_recruitment: form?.objective_recruitment !== '' ? Number(form?.objective_recruitment) : 0,
        diamonds_m1: form?.diamonds_m1 !== '' ? Number(form?.diamonds_m1) : 0,
      }, { onConflict: 'manager_id,server,period' });
      setSaving(false);
      if (err) { setError(err?.message); return; }
    }

    addToast(t('admin.objectives.editModal.objectiveUpdated', 'Objectif mis à jour'), 'success');
    onSaved();
    onClose();
  };

  const inputStyle = {
    backgroundColor: '#111',
    border: '1px solid #2a2a2a',
    color: 'rgba(255,255,255,0.85)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '16px',
    width: '100%',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
  };
  const labelStyle = { color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '4px', fontFamily: 'Inter, sans-serif' };
  const readonlyStyle = { ...inputStyle, backgroundColor: '#0d0d0d', color: 'rgba(255,255,255,0.4)', cursor: 'default' };

  const MONTH_NAMES = {
    '01': t('months.0', 'January'), '02': t('months.1', 'February'), '03': t('months.2', 'March'), '04': t('months.3', 'April'),
    '05': t('months.4', 'May'), '06': t('months.5', 'June'), '07': t('months.6', 'July'), '08': t('months.7', 'August'),
    '09': t('months.8', 'September'), '10': t('months.9', 'October'), '11': t('months.10', 'November'), '12': t('months.11', 'December'),
  };

  const periodDisplay = (() => {
    if (!objective?.period) return '—';
    const [y, m] = objective?.period?.split('-');
    return `${MONTH_NAMES?.[m] || m} ${y}`;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="rounded-xl p-6 w-full max-w-md" style={{ backgroundColor: '#111', border: '1px solid #222' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.objectives.editModal.title', 'Modifier l\'objectif')}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <p style={labelStyle}>{t('admin.objectives.editModal.manager', 'Manager')}</p>
            <input type="text" value={objective?.manager_name || '—'} readOnly style={readonlyStyle} />
          </div>
          <div>
            <p style={labelStyle}>{t('admin.objectives.editModal.server', 'Serveur')}</p>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}>
              <ServerBadge serverId={objective?.server} servers={servers} />
            </div>
          </div>
          <div>
            <p style={labelStyle}>{t('admin.objectives.editModal.period', 'Période')}</p>
            <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: '#0d0d0d', border: '1px solid #2a2a2a' }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
                {periodDisplay}
              </span>
            </div>
          </div>
          <div>
            <p style={labelStyle}>{t('admin.objectives.editModal.objectiveDiamonds', 'Objectif Diamonds')}</p>
            <input type="number" value={form?.objective_diamonds} onChange={(e) => setForm({ ...form, objective_diamonds: e?.target?.value })} style={inputStyle} placeholder="0" />
          </div>
          <div>
            <p style={labelStyle}>{t('admin.objectives.editModal.objectiveRecruitment', 'Objectif Recrutement')}</p>
            <input type="number" value={form?.objective_recruitment} onChange={(e) => setForm({ ...form, objective_recruitment: e?.target?.value })} style={inputStyle} placeholder="0" />
          </div>
          <div>
            <p style={labelStyle}>{t('admin.objectives.editModal.diamondsM1', 'Diamonds M-1')}</p>
            <input type="number" value={form?.diamonds_m1} onChange={(e) => setForm({ ...form, diamonds_m1: e?.target?.value })} style={inputStyle} placeholder="0" />
          </div>
          {error && <p className="text-sm" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>{error}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.6)', border: '1px solid #2a2a2a', fontFamily: 'Inter, sans-serif' }}>
            {t('admin.objectives.editModal.cancel', 'Annuler')}
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2 rounded-lg text-sm font-bold transition-colors" style={{ backgroundColor: 'var(--color-primary)', color: '#fff', fontFamily: 'Inter, sans-serif', opacity: saving ? 0.7 : 1 }}>
            {saving ? t('admin.objectives.editModal.saving', 'Enregistrement...') : t('admin.objectives.editModal.save', 'Enregistrer')}
          </button>
        </div>
      </div>
    </div>
  );
};

const getMonths = (t) => [
  { value: '01', label: t('months.0', 'January') }, { value: '02', label: t('months.1', 'February') },
  { value: '03', label: t('months.2', 'March') }, { value: '04', label: t('months.3', 'April') },
  { value: '05', label: t('months.4', 'May') }, { value: '06', label: t('months.5', 'June') },
  { value: '07', label: t('months.6', 'July') }, { value: '08', label: t('months.7', 'August') },
  { value: '09', label: t('months.8', 'September') }, { value: '10', label: t('months.9', 'October') },
  { value: '11', label: t('months.10', 'November') }, { value: '12', label: t('months.11', 'December') },
];

// Main Page
const ManagerObjectivesManagement = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const MONTHS = getMonths(t);
  const initPeriod = getCurrentPeriod();
  const [selectedYear, setSelectedYear] = useState(initPeriod?.split('-')?.[0]);
  const [selectedMonth, setSelectedMonth] = useState(initPeriod?.split('-')?.[1]);

  const selectedPeriod = `${selectedYear}-${selectedMonth}`;

  const [serverFilter, setServerFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [objectiveFilter, setObjectiveFilter] = useState('');

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [rows, setRows] = useState([]);
  const [managers, setManagers] = useState([]);
  const [servers, setServers] = useState([]);
  const [m1Map, setM1Map] = useState({});

  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState(null);

  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev?.filter((t) => t?.id !== id)), 3500);
  }, []);

  // Auth
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) setUserEmail(user?.email || '');
    };
    fetchUser();
  }, []);

  // Fetch servers
  const fetchServers = useCallback(async () => {
    const { data } = await supabase?.from('servers')?.select('id, name, color')?.eq('is_active', true);
    setServers(data || []);
  }, []);

  // Fetch managers
  const fetchManagers = useCallback(async () => {
    const { data } = await supabase?.from('users')?.select('id, name, managed_servers')?.in('role', ['manager', 'region_manager'])?.order('name');
    setManagers(data || []);
    return data || [];
  }, []);

  // Fetch creator_monthly for previous period of selectedPeriod to build m1Map
  const fetchM1Map = useCallback(async (period) => {
    const prevPeriod = getPrevPeriod(period);
    if (!prevPeriod) return {};
    const { data: m1Data } = await supabase
      ?.from('creator_monthly')
      ?.select('manager_id, server, diamonds')
      ?.eq('period', prevPeriod);
    const map = {};
    m1Data?.forEach((r) => {
      const key = `${r?.manager_id}__${r?.server}`;
      if (!map?.[key]) map[key] = 0;
      map[key] += r?.diamonds || 0;
    });
    setM1Map(map);
    return map;
  }, []);

  // Build combined rows — all managers + left join objectives filtered by selectedPeriod
  const buildRows = useCallback(async (allManagers, m1MapData, period) => {
    const { data: objectives } = await supabase
      ?.from('manager_objectives')
      ?.select('*');

    const combined = [];
    allManagers?.forEach((manager) => {
      (manager?.managed_servers || [])?.forEach((server) => {
        const obj = objectives?.find(
          (o) => o?.manager_id === manager?.id && o?.server === server && o?.period === period
        );
        combined?.push({
          manager_id: manager?.id,
          manager_name: manager?.name,
          server,
          period: period,
          objective_id: obj?.id || null,
          objective_diamonds: obj?.objective_diamonds ?? 0,
          objective_recruitment: obj?.objective_recruitment ?? 0,
          diamonds_m1: m1MapData?.[`${manager?.id}__${server}`] ?? (obj?.diamonds_m1 ?? 0),
          has_objective: !!obj,
        });
      });
    });
    setRows(combined);
  }, []);

  const loadAll = useCallback(async (period) => {
    setLoading(true);
    const [allManagers, m1MapData] = await Promise.all([fetchManagers(), fetchM1Map(period), fetchServers()]);
    await buildRows(allManagers, m1MapData, period);
    setLoading(false);
  }, [fetchManagers, fetchM1Map, fetchServers, buildRows]);

  useEffect(() => {
    loadAll(selectedPeriod);
  }, [selectedPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inline save — upsert for rows without objective
  const handleInlineSave = async (row, field, value) => {
    if (row?.objective_id) {
      const { error } = await supabase?.from('manager_objectives')?.update({
        [field]: value,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', row?.objective_id);
      if (error) { addToast('Erreur: ' + error?.message, 'error'); return; }
    } else {
      const upsertData = {
        manager_id: row?.manager_id,
        server: row?.server,
        period: row?.period,
        objective_diamonds: row?.objective_diamonds ?? 0,
        objective_recruitment: row?.objective_recruitment ?? 0,
        diamonds_m1: row?.diamonds_m1 ?? 0,
        [field]: value,
      };
      const { error } = await supabase?.from('manager_objectives')?.upsert(upsertData, { onConflict: 'manager_id,server,period' });
      if (error) { addToast('Erreur: ' + error?.message, 'error'); return; }
    }
    addToast(t('admin.objectives.updated', 'Mis à jour'), 'success');
    loadAll(selectedPeriod);
  };

  // Sort toggle handler
  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') { setSortField(null); setSortDir('asc'); }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4, color: '#E5202A' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Filtered + sorted rows
  let filtered = rows?.filter((row) => {
    if (serverFilter && row?.server !== serverFilter) return false;
    if (managerFilter && row?.manager_id !== managerFilter) return false;
    if (objectiveFilter === 'with' && !row?.has_objective) return false;
    if (objectiveFilter === 'without' && row?.has_objective) return false;
    return true;
  });

  if (sortField) {
    filtered = [...filtered]?.sort((a, b) => {
      const av = a?.[sortField] ?? 0;
      const bv = b?.[sortField] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }

  // Summary cards
  const managersWithObj = new Set(rows?.filter((r) => r?.has_objective)?.map((r) => r?.manager_id))?.size;
  const totalObjDiamonds = rows?.filter((r) => r?.has_objective)?.reduce((s, r) => s + (r?.objective_diamonds || 0), 0);

  const cardStyle = {
    backgroundColor: '#111',
    border: '1px solid #1f1f1f',
    borderRadius: '12px',
    padding: '20px',
  };

  const thStyle = {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: 'Inter, sans-serif',
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '10px 14px',
    fontSize: '13px',
    color: 'rgba(255,255,255,0.75)',
    fontFamily: 'Inter, sans-serif',
    borderBottom: '1px solid #1a1a1a',
  };

  const selectStyle = {
    backgroundColor: '#111',
    border: '1px solid #2a2a2a',
    color: 'rgba(255,255,255,0.75)',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '13px',
  };

  const periodSelectStyle = {
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '8px',
    padding: '8px 12px',
    color: 'white',
    fontSize: '13px',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    cursor: 'pointer',
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} userEmail={userEmail} />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.objectives.title', 'Objectifs Managers')} />
        {/* Header with period selector */}
        <div className="flex-shrink-0 px-6 py-5 border-b" style={{ borderColor: '#1f1f1f', backgroundColor: '#0d0d0d' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-white font-bold text-2xl" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.objectives.title', 'Objectifs Managers')}</h1>
              <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
                {t('admin.objectives.subtitle', 'Gestion des objectifs par manager et par serveur')}
              </p>
            </div>
            {/* Period selector in header */}
            <div className="flex items-center gap-2">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e?.target?.value)}
                style={periodSelectStyle}
              >
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="2027">2027</option>
              </select>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e?.target?.value)}
                style={periodSelectStyle}
              >
                {MONTHS?.map((m) => (
                  <option key={m?.value} value={m?.value}>{m?.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div style={cardStyle}>
              <p className="text-xs font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('admin.objectives.managersWithObjectives', 'Managers avec objectifs')}
              </p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{managersWithObj}</p>
            </div>
            <div style={cardStyle}>
              <p className="text-xs font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('admin.objectives.totalObjectiveDiamonds', 'Total Objectif Diamonds')}
              </p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{formatNum(totalObjDiamonds)}</p>
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <select value={serverFilter} onChange={(e) => setServerFilter(e?.target?.value)} style={selectStyle}>
              <option value="">{t('admin.objectives.allServers', 'Tous les serveurs')}</option>
              {servers?.map((s) => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
            </select>
            <select value={managerFilter} onChange={(e) => setManagerFilter(e?.target?.value)} style={selectStyle}>
              <option value="">{t('admin.objectives.allManagers', 'Tous les managers')}</option>
              {managers?.map((m) => <option key={m?.id} value={m?.id}>{m?.name}</option>)}
            </select>
            <select value={objectiveFilter} onChange={(e) => setObjectiveFilter(e?.target?.value)} style={selectStyle}>
              <option value="">{t('admin.objectives.filterAll', 'Tous')}</option>
              <option value="with">{t('admin.objectives.filterWith', 'Avec objectifs')}</option>
              <option value="without">{t('admin.objectives.filterWithout', 'Sans objectifs')}</option>
            </select>
          </div>

          {/* Table */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1f1f1f', backgroundColor: '#0d0d0d' }}>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-6 h-6 rounded-full border-2 border-red-500 border-t-transparent animate-spin" />
              </div>
            ) : filtered?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="18" stroke="rgba(255,255,255,0.1)" strokeWidth="2" />
                  <path d="M14 20h12M20 14v12" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  {t('admin.objectives.noObjectiveFound', 'Aucun objectif trouvé')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f', backgroundColor: '#0a0a0a' }}>
                      <th style={thStyle}>{t('admin.objectives.colManager', 'Manager')}</th>
                      <th style={thStyle}>{t('admin.objectives.colServer', 'Serveur')}</th>
                      <th style={thStyle}>{t('admin.objectives.colPeriod', 'Période')}</th>
                      <th
                        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('diamonds_m1')}
                      >
                        {t('admin.objectives.colDiamondsM1', 'Diamonds M-1')} <SortIcon field="diamonds_m1" />
                      </th>
                      <th
                        style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('objective_diamonds')}
                      >
                        {t('admin.objectives.colObjectiveDiamonds', 'Objectif Diamonds')} <SortIcon field="objective_diamonds" />
                      </th>
                      <th style={thStyle}>{t('admin.objectives.colObjectiveRecruitment', 'Objectif Recrutement')}</th>
                      <th style={thStyle}>{t('admin.objectives.colActions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered?.map((row, idx) => (
                      <tr
                        key={`${row?.manager_id}__${row?.server}__${idx}`}
                        className="hover:bg-white/[0.02] transition-colors"
                        style={{
                          // FIX 3: orange left border for rows without objective
                          borderLeft: !row?.has_objective ? '3px solid #f97316' : '3px solid transparent',
                        }}
                      >
                        <td style={tdStyle}>
                          <span className="font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>
                            {row?.manager_name || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <ServerBadge serverId={row?.server} servers={servers} />
                        </td>
                        <td style={tdStyle}>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontFamily: 'Inter, sans-serif' }}>
                            {row?.period || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatNum(row?.diamonds_m1)}</td>
                        <td style={tdStyle}>
                          <InlineEditCell
                            value={row?.objective_diamonds}
                            onSave={(val) => handleInlineSave(row, 'objective_diamonds', val)}
                          />
                        </td>
                        <td style={tdStyle}>
                          <InlineEditCell
                            value={row?.objective_recruitment}
                            onSave={(val) => handleInlineSave(row, 'objective_recruitment', val)}
                          />
                        </td>
                        <td style={tdStyle}>
                          <button
                            onClick={() => setEditRow(row)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M8.5 1.5l2 2L4 10H2v-2L8.5 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t('admin.objectives.edit', 'Modifier')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Edit Modal */}
      {editRow && (
        <EditModal
          objective={editRow}
          servers={servers}
          onClose={() => setEditRow(null)}
          onSaved={() => loadAll(selectedPeriod)}
          addToast={addToast}
        />
      )}
      <Toast toasts={toasts} />
      <AdminBottomNav />
    </div>
  );
};

export default ManagerObjectivesManagement;
